import isEqual from 'lodash/isEqual.js'
import pick from 'lodash/pick.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
} from '../../../../store.js'
import {TAG_FILTER_OR} from '../../../../common/constants/Tags.js'
import {clearTagFilterPanelSearch} from './TagFilterPanel.js'
import {clearFilterPanelSearch} from '../../../OrderListPage/Modals/OrderListFilterModal/FilterPanel.js'
import {
  navigateStocktakeDetail,
  updateProductFilters,
  updateCategory,
  updateSearchText,
  updateProductCartIDs,
  updateProductSupplierIDs,
  updateFulfillmentChannelFilters,
  updateProductTagFilters,
  updateProductUntagged,
  updateProductTagFilterBy,
  updateProductExcludeTags,
  cleanStocktakeDetailQuery,
} from '../../../StocktakeDetailPage/stocktakeDetailFunctions.js'
import deferPromise from '../../../../common/deferPromise.js'
import {ensureTroolean} from '../../../../common/ensure.js'
import {PRODUCT_FULFILLMENT_CHANNELS} from '../../../../common/constants/Products.js'
import {clearStatusFilterPanelSearch} from './FiltersPanel.js'
import {STOCKTAKE_LINE_TROOLEAN_FILTERS} from '../../../../common/constants/Stocktakes.js'

const MODAL_FORM = 'stocktake_detail_filter_modal'

export const FILTERS_PANEL = 'filters'
export const SALES_CHANNEL_FILTER_PANEL = 'sales_channel'
export const SUPPLIER_FILTER_PANEL = 'supplier'
export const TAG_FILTER = 'tag'
export const MISC_FILTER_PANEL = 'misc'
export const STOCKTAKE_DETAIL_FILTER_OPTIONS = [
  {value: FILTERS_PANEL, display: 'Status'},
  {value: SALES_CHANNEL_FILTER_PANEL, display: 'Sales Channels'},
  {value: SUPPLIER_FILTER_PANEL, display: 'Suppliers'},
  {value: TAG_FILTER, display: 'Tags'},
  {value: MISC_FILTER_PANEL, display: 'Misc'},
]

export async function showStocktakeDetailFilterModal({query = {}} = {}) {
  const {updates} = await showStocktakeDetailFilterModalPrime({
    query,
    singleRun: true,
  })

  return await navigateStocktakeDetail(updates)
}

export async function showProductQueryFilterModal({
  query = {},
  singleRun = true,
} = {}) {
  const {originalForm, updates} = await showStocktakeDetailFilterModalPrime({
    query,
    singleRun,
  })

  if (!updates) {
    return query
  }

  const productQuery = cleanStocktakeDetailQuery({...originalForm, ...updates})

  delete productQuery.sort
  delete productQuery.limit
  delete productQuery.offset

  return productQuery
}

export async function showStocktakeDetailFilterModalPrime({
  query = {},
  singleRun = true,
  deferredPromise = deferPromise(),
  originalForm,
} = {}) {
  const form = {
    search: query.search || '',
    category: query.category || '',
    ...STOCKTAKE_LINE_TROOLEAN_FILTERS.reduce((prev, key) => {
      prev[key] = ensureTroolean(query[key])

      return prev
    }, {}),
    ...PRODUCT_FULFILLMENT_CHANNELS.reduce((prev, key) => {
      prev[key] = ensureTroolean(query[key])

      return prev
    }, {}),
    sales_channel: query.sales_channel || [],
    supplier: query.supplier || [],
    tag: query.tag || [],
    untagged: query.untagged || false,
    tag_filter_by: query.tag_filter_by || TAG_FILTER_OR,
    exclude_tags: query.exclude_tags || [],
  }

  setForm(MODAL_FORM, {
    singleRun,
    deferredPromise,
    originalForm: originalForm || {...form},
    ...form,
  })

  clearFilterPanelSearch(SALES_CHANNEL_FILTER_PANEL)
  clearFilterPanelSearch(SUPPLIER_FILTER_PANEL)
  clearTagFilterPanelSearch()
  clearStatusFilterPanelSearch()

  return deferredPromise.promise
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal(updates) {
  const {originalForm, deferredPromise} = modalFormSelector(getState())

  deferredPromise.resolve({originalForm, updates})

  removeForm(MODAL_FORM)
}

export function clearFilters() {
  const {singleRun, deferredPromise, originalForm} =
    modalFormSelector(getState())

  // re-setup modal form with most of the pieces reused
  // reuse deferredPromise so that any awaits from the original call to show... will resolve
  // reuse originalForm so applyFilter() has the correct thing to compare against
  // reuse singleRun because that is part of the context
  showStocktakeDetailFilterModalPrime({
    singleRun,
    deferredPromise,
    originalForm,
  })
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export function errorsSelector() {
  const errors = {}

  return errors
}

export async function applyFilter() {
  const {
    originalForm,
    search,
    category,
    sales_channel,
    supplier,
    tag,
    untagged,
    tag_filter_by,
    exclude_tags,
    ...form
  } = modalFormSelector(getState())

  const filters = pick(form, STOCKTAKE_LINE_TROOLEAN_FILTERS)
  const fulfillmentChannelFilters = pick(form, PRODUCT_FULFILLMENT_CHANNELS)
  let updates = {}

  if (search !== originalForm.search) {
    updates = {...updates, ...updateSearchText(search)}
  }

  if (category !== originalForm.category) {
    updates = {...updates, ...updateCategory(category)}
  }

  if (!isEqual(filters, pick(originalForm, STOCKTAKE_LINE_TROOLEAN_FILTERS))) {
    updates = {...updates, ...updateProductFilters(filters)}
  }

  if (
    !isEqual(
      fulfillmentChannelFilters,
      pick(originalForm, PRODUCT_FULFILLMENT_CHANNELS),
    )
  ) {
    updates = {
      ...updates,
      ...updateFulfillmentChannelFilters(fulfillmentChannelFilters),
    }
  }

  if (!isEqual(sales_channel, originalForm.sales_channel)) {
    updates = {...updates, ...updateProductCartIDs(sales_channel)}
  }

  if (!isEqual(supplier, originalForm.supplier)) {
    updates = {...updates, ...updateProductSupplierIDs(supplier)}
  }

  if (!isEqual(tag, originalForm.tag)) {
    updates = {...updates, ...updateProductTagFilters(tag)}
  }

  if (untagged !== originalForm.untagged) {
    updates = {...updates, ...updateProductUntagged(untagged)}
  }

  if (tag_filter_by !== originalForm.tag_filter_by) {
    updates = {...updates, ...updateProductTagFilterBy(tag_filter_by)}
  }

  if (!isEqual(exclude_tags, originalForm.exclude_tags)) {
    updates = {...updates, ...updateProductExcludeTags(exclude_tags)}
  }

  closeModal(updates)
}
